import { SessionKey, SessionStorage } from "../types";
import { SessionKeys } from "../utils/constants";
import type { SupportedLanguage } from "../types/language";

// Set a session item by key
export const setSessionItem = async <T extends SessionKey>(
  key: T,
  value: SessionStorage[T],
): Promise<void> => {
  try {
    await localStorage.setItem(SessionKeys[key], JSON.stringify(value));
  } catch (error) {
    console.error("Error setting session item:", error);
  }
};

// Get a session item by key
export const getSessionItem = async <T extends SessionKey>(
  key: T,
): Promise<SessionStorage[T] | null> => {
  try {
    const item = await localStorage.getItem(SessionKeys[key]);
    return item ? (JSON.parse(item) as SessionStorage[T]) : null;
  } catch (error) {
    console.error("Error getting session item:", error);
    return null;
  }
};

// Helper function to retrieve multiple session items by keys with decryption
export const getMultiSessionItem = async (
  keys: string[],
): Promise<{ [key: string]: any } | null> => {
  try {
    const result: { [key: string]: any } = {};

    for (const originalKey of keys) {
      // Map known keys if present, otherwise use the key as-is
      const keyName =
        (SessionKeys as Record<string, string>)[originalKey] || originalKey;
      const item = await localStorage.getItem(keyName);
      if (item == null) continue;

      try {
        let parsed: any = JSON.parse(item);
        // Handle double-stringified payloads
        if (
          typeof parsed === "string" &&
          (parsed.trim().startsWith("{") || parsed.trim().startsWith("["))
        ) {
          try {
            parsed = JSON.parse(parsed);
          } catch {}
        }
        result[originalKey] = parsed;
      } catch (e) {
        // Gracefully handle invalid JSON strings like 'undefined'/'null'
        const trimmed = item.trim();
        if (trimmed === "undefined" || trimmed === "null" || trimmed === "") {
          continue;
        }
        // Fallback to raw string
        result[originalKey] = item;
      }
    }

    return result;
  } catch (error) {
    console.error("Error getting multiple session items:", error);
    return null;
  }
};

// Remove a session item by key
export const removeSessionItem = async (key: SessionKey): Promise<void> => {
  try {
    await localStorage.removeItem(SessionKeys[key]);
  } catch (error) {
    console.error("Error removing session item:", error);
  }
};

export const calculateAge = (dob: string) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

// utils/timeAgo.js

const formatRelativeUnit = (
  value: number,
  language: SupportedLanguage,
  englishSingular: string,
  englishPlural: string,
  telSingular: string,
  telPlural: string,
) => {
  if (language === "te") {
    return `${value} ${value === 1 ? telSingular : telPlural} క్రితం`;
  }
  return `${value} ${value === 1 ? englishSingular : englishPlural} ago`;
};

export const timeAgo = (
  dateInput: string,
  language: SupportedLanguage = "en",
) => {
  if (!dateInput) return language === "te" ? "—" : "—";

  const date = new Date(dateInput);
  const now = new Date();

  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return language === "te" ? "ఇప్పుడే" : "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return formatRelativeUnit(
      minutes,
      language,
      "min",
      "mins",
      "నిమిషం",
      "నిమిషాలు",
    );
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return formatRelativeUnit(hours, language, "hour", "hours", "గంట", "గంటలు");
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return formatRelativeUnit(days, language, "day", "days", "రోజు", "రోజులు");
  }

  const weeks = Math.floor(days / 7);
  if (weeks < 4) {
    return formatRelativeUnit(
      weeks,
      language,
      "week",
      "weeks",
      "వారం",
      "వారాలు",
    );
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return formatRelativeUnit(
      months,
      language,
      "month",
      "months",
      "నెల",
      "నెలలు",
    );
  }

  const years = Math.floor(days / 365);
  return formatRelativeUnit(
    years,
    language,
    "year",
    "years",
    "సంవత్సరం",
    "సంవత్సరాలు",
  );
};

// Helper function to convert string to boolean
export const convertStringToBoolean = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
};

export const toSlug = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // spaces → -
    .replace(/[^a-z0-9-]/g, "") // remove special chars
    .replace(/-+/g, "-"); // avoid multiple -
